<template>
  <div name="pupilmove" class="-mx-3 -mt-4 pb-10 pt-4">
    <section v-if="errored" class="w-full text-center mt-2">
      <h3 class="font-bold text-xl text-action-red">
        There was an issue adding the pupil details.
      </h3>
      <p class="text-action-blue">
        Please try again shortly or contact us to resolve the issue.
      </p>
    </section>

    <transition name="fade">
      <div class="loading" v-show="isSaving">
        <span class="fa fa-spinner fa-spin"></span> Saving
      </div>
    </transition>

    <table class="table-wrapper w-95p bg-white mx-auto rounded shadow-sm">
      <thead class="text-primary bg-blue-100 h-12">
        <tr>
          <th width="5%">Year Group</th>
          <th width="30%" class="pl-2 text-left">Name</th>
          <th width="19%" class="text-left">Class</th>
          <th width="10%"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          class="course border"
          v-for="pupil in selectedPupils"
          :key="pupil.identifier"
        >
          <td width="10%">
            {{ pupilYearGroupDisplay(pupil) }}
          </td>
          <td width="30%">{{ pupil.name }}</td>
          <td width="20%">{{ pupil.inclass }}</td>

          <td
            width="10%"
            class="text-center text-xs cursor-pointer text-blue-400"
          ></td>
        </tr>
      </tbody>
      <tfoot></tfoot>
    </table>

    <div class="">
    <div class="grid grid-cols-1 space-y-1 py-4 mt-2 border-b border-gray-300">
      <div class="mx-6">
        <label class="font-semibold">Set new yeargroup:</label>
        <a
          class="text-xs cursor-pointer right text-red-400 mb-2"
          @click="yearMoveSelected = {}"
          >Clear</a
        >

        <ul class="mt-2 w-full">
          <li
            v-for="item in yearList"
            :key="item.id"
            class="inline-block mr-2"
          >
            <button
              type="button"
              @click="yearMoveSelected = item"
              class="selection-button mb-3 hover:bg-opacity-50 wide"
              v-bind:class="
                item.id == yearMoveSelected.id
                  ? 'filter-item-button-on bg-gray-500 '
                  : 'filter-item-button-off'
              "
            >
              <i
                class="far pr-1"
                v-bind:class="
                  item.id == yearMoveSelected.id
                    ? 'fa-check-circle'
                    : 'fa-circle'
                "
              ></i>
              {{ item.name }}
            </button>
          </li>
        </ul>
      </div>
    </div>

    <div class="grid grid-cols-2 space-y-1 py-4 border-b border-gray-300">
      <div class="ml-6 pr-4 border-r">
        <label class="font-semibold mb-2 pb-2"> Class / Group</label>
        <div class="mt-2 -ml-1">
          <ClassDropdown @classValueChanged="classSelect"></ClassDropdown>
        </div>
      </div>

      <div class="pt-6 mx-4">
        <input
          v-model="newClass"
          class="mb-2"
          id="grid-sen"
          type="text"
          placeholder="Or enter new class..."
        />
      </div>
    </div>

    <div class="h-10 text-left mt-4 ml-6 mb-10">
      <button
        @click="submitMove()"
        type="button"
        class="publish-button hover:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed px-4"
        :disabled="isMoveDisabled"
      >
        Move Pupils
      </button>
    </div>
  </div>

  </div>
</template>

<script>
import { PupilService } from "@/services/pupil.service";

const ClassDropdown = () => import("@/components/pupil/ClassDropdown.vue");
import { yearGroupMixin } from "@/mixins/yearGroupMixin.js";

export default {
  name: "pupilmove",
  mixins: [yearGroupMixin],
  components: {
    ClassDropdown,
  },
  props: {
    pupils: { type: Array, required: true },
  },
  computed: {
    isMoveDisabled() {
      return (
        this.yearMoveSelected.id == undefined &&
        (this.classMoveSelected.id == undefined || this.classMoveSelected.id === 0) &&
        this.newClass.length == 0
      );
    },
    yearList() {
      return this.years
    }
  },
  data() {
    return {
      yearMoveSelected: {},
      classMoveSelected: {},
      errored: false,
      newClass: "",
      isSaving: false,
      selectedPupils: this.pupils,
    };
  },
  methods: {
    alertDisplay() {
      this.$swal({
        title: "Please complete all fields",
        text: "You must complete the form.",
        type: "warning",
        showCancelButton: false,
        showCloseButton: true,
        showLoaderOnConfirm: true,
      });
    },
    submitMove() {
      this.errored = false;
      if ( this.isMoveDisabled ) {
        this.alertDisplay();
      } else {
        this.moveClass();
      }
    },
    moveClass() {
      this.isSaving = true;
      let vm = this;
      vm.pupils.forEach(function (pp, index) {


        if (vm.newClass.length > 0) {
          pp.inclass = vm.newClass;
        } else if (vm.classMoveSelected.id != undefined && vm.classMoveSelected.id > 0) {
          pp.inclass = vm.classMoveSelected.name;
        } else {
          pp.inclass = pp.inclass
        }

        if (vm.yearMoveSelected.id != undefined) {
          pp.year_id = vm.yearMoveSelected.id;
        } else {
          pp.year_id = pp.year.id
        }

        PupilService.update(pp)
          .then((response) => {
            if (index == vm.selectedPupils.length - 1) {
              vm.selectedPupils = [];
            }
            vm.yearMoveSelected = {};
            vm.classMoveSelected = {};
            vm.newClass = "";
          })
          .catch((err) => {
            this.failure(err);
          })
          .finally(() => {
            vm.closeMovePupils();
          });
      });
    },
    closeMovePupils() {
      this.isSaving = false;
      this.$emit('pupil-moved')
    },
    classSelect(value) {
      this.classMoveSelected = value;
    },
    failure(error) {
      this.isSaving = false;
      this.errored = true
      console.log("Pupil update error " + error);
    },
  },
};
</script>

<style scoped>
input:focus,
textarea:focus,
select:focus {
  outline: none;
}

input[type="text"] {
  @apply m-0 px-0 py-2 border-none h-auto rounded-none;
}

input[type="text"]:focus {
  border-bottom: 1px solid #a0aec0;
}

::-webkit-input-placeholder {
  color: #a0aec0 !important;
  opacity: 0.8;
}

:-moz-placeholder {
  color: #a0aec0 !important;
  opacity: 0.8;
}

::-moz-placeholder {
  color: #a0aec0 !important;
  opacity: 0.8;
}

:-ms-input-placeholder {
  color: #a0aec0 !important;
  opacity: 0.8;
}
</style>
